/* District Bangle — product.js
   Variant selection: client-side option matching that updates the hidden id
   input, price, availability, and URL — no network round-trip. Degrades to a
   native <select> POST with JS off (the snippet renders real inputs either way).
   The picker never invents prices; the add button reflects real availability. */
import { BaseElement, define, money } from '@theme/global';

class VariantSelects extends BaseElement {
  init() {
    this.variants = this.readVariants();
    this.scope = this.closest('[data-product-root]') || document;
    this.listen(this, 'change', () => this.onChange());
    this.onChange(true);
  }

  readVariants() {
    const node = this.querySelector('[data-variant-json]');
    if (!node) return [];
    try { return JSON.parse(node.textContent); } catch (e) { console.error('variant json', e); return []; }
  }

  selectedOptions() {
    // One value per option position, in schema order.
    const groups = this.querySelectorAll('[data-option-index]');
    const values = [];
    groups.forEach((group) => {
      const idx = Number(group.dataset.optionIndex);
      const checked = group.querySelector('input:checked, option:checked');
      const active = checked || group.querySelector('select')?.selectedOptions[0];
      if (active) values[idx] = active.value;
    });
    return values;
  }

  match(values) {
    return this.variants.find((v) => v.options.every((opt, i) => opt === values[i]));
  }

  reflectLabels() {
    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const label = group.querySelector('[data-selected-for]');
      const active = group.querySelector('input:checked, option:checked')
        || group.querySelector('select')?.selectedOptions[0];
      if (label && active) label.textContent = active.value;
    });
  }

  onChange(initial) {
    this.reflectLabels();
    const variant = this.match(this.selectedOptions());
    const idInput = this.scope.querySelector('input[name="id"]');
    const priceNode = this.scope.querySelector('[data-price]');
    const addBtn = this.scope.querySelector('[data-add-button]');
    const addLabel = addBtn?.querySelector('[data-add-label]') || addBtn;

    if (!variant) {
      if (addBtn) { addBtn.disabled = true; }
      if (addLabel) addLabel.textContent = window.theme.strings.unavailable || 'Unavailable';
      return;
    }
    if (idInput) idInput.value = variant.id;
    if (priceNode) {
      const sale = variant.compare_at_price > variant.price;
      priceNode.classList.toggle('price--on-sale', sale);
      priceNode.innerHTML = `<span class="price__current">${money(variant.price)}</span>` +
        (sale ? `<s class="price__compare">${money(variant.compare_at_price)}</s>` : '');
    }
    if (addBtn) {
      addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available
        ? (window.theme.strings.addToCart || 'Add to cart')
        : (window.theme.strings.soldOut || 'Sold out');
    }
    // Reflect the selection in the URL without a reload (skip on first paint).
    if (!initial && variant.id && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }
}

define('variant-selects', VariantSelects);
